import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import localforage from "localforage";
import { useCallback, useEffect } from "react";
import { useGetCurrentVenue } from "./use-get-current-venue";
import { gqlReq } from "~/libs/gql";
import { STORAGE_KEYS } from "~/libs/storage-keys";
import { useVenueId } from "~/store";
import { useCustomerSessionStore } from "~/store/customer-session";
import {
	CustomerLoginDocument,
	CustomerLoginMutationVariables,
	CustomerSignUpDocument,
	CustomerSignUpMutationVariables,
	GetCurrentCustomerDocument,
} from "~/types/__generated/gql/graphql";

const CustomerSessionFragment = /* GraphQL */ `
	fragment CustomerSessionFragment on Customer {
		_id
		first_name
		last_name
		email
		phone_number
		username
	}
`;

void (
	/* GraphQL */ `
		query GetCurrentCustomer {
			getCurrentCustomer {
				...CustomerSessionFragment
			}
		}
		mutation CustomerLogin($identifier: String!, $password: String!) {
			customerLogin(identifier: $identifier, password: $password) {
				token
				customer {
					...CustomerSessionFragment
				}
			}
		}
		mutation CustomerSignUp($input: CustomerSignUpInput!) {
			customerSignUp(input: $input) {
				token
				customer {
					...CustomerSessionFragment
				}
			}
		}
		${CustomerSessionFragment}
	`
);

export const useCustomerSession = () => {
	const venueId = useVenueId();
	const { data: venue } = useGetCurrentVenue();
	const client = useQueryClient();
	const loginEnabled = !!venue?.is_digital_menu_user_login_enabled;
	const token = useCustomerSessionStore((state) =>
		venueId ? state.tokens[venueId] : null,
	);

	useEffect(() => {
		if (venueId) void useCustomerSessionStore.getState().hydrate(venueId);
	}, [venueId]);

	const clearSession = useCallback(async () => {
		if (!venueId) return;
		await client.cancelQueries({ queryKey: ["customer", venueId] });
		await localforage.removeItem(STORAGE_KEYS.CUSTOMER_TOKEN(venueId));
		useCustomerSessionStore.getState().setToken(venueId, null);
		client.removeQueries({
			predicate: (query) =>
				query.queryKey[0] === "customer" && query.queryKey[1] === venueId,
		});
	}, [client, venueId]);

	const customerQuery = useQuery({
		queryKey: ["customer", venueId, "current", token],
		enabled: loginEnabled && !!token,
		queryFn: async ({ signal }) => {
			const customer = await gqlReq({
				document: GetCurrentCustomerDocument,
				signal,
			});
			if (
				!customer &&
				!signal?.aborted &&
				useCustomerSessionStore.getState().tokens[venueId!] === token
			) {
				await localforage.removeItem(STORAGE_KEYS.CUSTOMER_TOKEN(venueId!));
				useCustomerSessionStore.getState().setToken(venueId!, null);
				await client.cancelQueries({
					queryKey: ["customer", venueId, "orders"],
				});
				await client.cancelQueries({ queryKey: ["customer", venueId, "qr"] });
				client.removeQueries({ queryKey: ["customer", venueId, "orders"] });
				client.removeQueries({ queryKey: ["customer", venueId, "qr"] });
			}
			return customer;
		},
	});

	const saveSession = async (
		session: Awaited<ReturnType<typeof loginRequest>>,
	) => {
		if (!venueId) throw new Error("Venue not found");
		await client.cancelQueries({ queryKey: ["customer", venueId] });
		await localforage.setItem(
			STORAGE_KEYS.CUSTOMER_TOKEN(venueId),
			session.token,
		);
		client.removeQueries({
			predicate: (query) =>
				query.queryKey[0] === "customer" && query.queryKey[1] === venueId,
		});
		client.setQueryData(
			["customer", venueId, "current", session.token],
			session.customer,
		);
		useCustomerSessionStore.getState().setToken(venueId, session.token);
	};
	const login = useMutation({
		mutationFn: loginRequest,
		onSuccess: saveSession,
	});
	const signUp = useMutation({
		mutationFn: (variables: CustomerSignUpMutationVariables) =>
			gqlReq({ document: CustomerSignUpDocument, variables }),
		onSuccess: saveSession,
	});
	const signOut = useMutation({ mutationFn: clearSession });

	return {
		customer: loginEnabled && token ? customerQuery.data : null,
		isLoading:
			(!!venueId && token === undefined) || customerQuery.isInitialLoading,
		login,
		signUp,
		signOut,
	};
};

const loginRequest = (variables: CustomerLoginMutationVariables) =>
	gqlReq({ document: CustomerLoginDocument, variables });
